import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export default function Payments() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] || "en";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [method, setMethod] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (method) params.set("method", method);
      params.set("page_size", "100");
      const { data } = await api.get(`/payments?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load payments"));
    } finally {
      setLoading(false);
    }
  }, [from, to, method]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6" data-testid="payments-page">
      <header className="space-y-1">
        <h1 className="font-heading text-3xl tracking-tight text-slate-900">
          {t("payments.title")}
        </h1>
        <p className="text-sm text-slate-500">
          {t("payments.subtitle", { count: total })}
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4 rounded-xl border border-slate-200 bg-white p-4">
        <div className="space-y-1">
          <Label className="text-xs">{t("payments.filters.from")}</Label>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            data-testid="payments-filter-from"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("payments.filters.to")}</Label>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid="payments-filter-to"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("payments.filters.method")}</Label>
          <Input
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            placeholder={t("payments.filters.methodPlaceholder")}
            data-testid="payments-filter-method"
          />
        </div>
        <div className="flex items-end">
          <Button
            onClick={load}
            data-testid="payments-refresh"
            className="w-full bg-slate-900 text-white hover:bg-slate-800"
          >
            {t("common.search")}
          </Button>
        </div>
      </div>

      <div className="mobile-cards overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 text-start">{t("payments.cols.date")}</th>
              <th className="px-4 py-3 text-start">{t("payments.cols.invoice")}</th>
              <th className="px-4 py-3 text-start">{t("payments.cols.customer")}</th>
              <th className="px-4 py-3 text-end">{t("payments.cols.amount")}</th>
              <th className="px-4 py-3 text-start">{t("payments.cols.method")}</th>
              <th className="px-4 py-3 text-start">{t("payments.cols.ocr")}</th>
            </tr>
          </thead>
          <tbody data-testid="payments-table">
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                  {t("common.loading")}
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                  {t("payments.empty")}
                </td>
              </tr>
            )}
            {!loading &&
              items.map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`payment-row-${p.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap" data-label={t("payments.cols.date")}>
                    {formatDate((p.paid_at || "").split("T")[0], lang)}
                  </td>
                  <td className="px-4 py-3" data-label={t("payments.cols.invoice")}>
                    <NavLink to={`/app/invoices/${p.invoice_id}`} className="font-mono text-slate-900 hover:underline">
                      {p.invoice_number}
                    </NavLink>
                  </td>
                  <td className="px-4 py-3" data-label={t("payments.cols.customer")}>{p.customer_name}</td>
                  <td className="px-4 py-3 text-end tabular-nums" data-label={t("payments.cols.amount")}>
                    {formatCurrency(p.amount, p.currency, lang)}
                  </td>
                  <td className="px-4 py-3" data-label={t("payments.cols.method")}>{p.method || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500" data-label={t("payments.cols.ocr")}>
                    {p.ocr_number || ""}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
